import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TenantActionSeverity, TenantActionType } from 'src/app/core/graphql/generated/gen-types';
import { TenantActionItem, TenantActionsService } from 'src/app/core/services/tenantActions/tenant-actions.service';

const SEVERITY_ORDER: Record<TenantActionSeverity, number> = {
	[TenantActionSeverity.Error]: 2,
	[TenantActionSeverity.Warning]: 1,
};

@Component({
	selector: 'app-tenant-action-banner',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatButtonModule, RouterModule, TranslateModule],
	templateUrl: './tenant-action-banner.component.html',
	styleUrl: './tenant-action-banner.component.scss',
})
export class TenantActionBannerComponent implements OnInit, OnDestroy {
	current: TenantActionItem | null = null;
	private sub?: Subscription;

	constructor(private tenantActions: TenantActionsService) {}

	ngOnInit(): void {
		this.sub = this.tenantActions.getTenantActions().subscribe((actions) => {
			this.current = this.pickHighestSeverityVisible(actions);
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	dismiss(): void {
		if (!this.current) return;
		try {
			sessionStorage.setItem(this.dismissKey(this.current), '1');
		} catch {
			// sessionStorage may be unavailable (private mode); fall back to in-memory clear
		}
		this.current = null;
	}

	iconName(): string {
		return this.current?.severity === TenantActionSeverity.Error ? 'error' : 'warning';
	}

	translationKey(): string {
		return this.current ? `TENANT_ACTIONS.${this.current.type}` : '';
	}

	get deepLinkPath(): string {
		return (this.current?.deepLinkRoute ?? '/').split('?')[0];
	}

	get deepLinkQueryParams(): Record<string, string> {
		const qs = (this.current?.deepLinkRoute ?? '').split('?')[1];
		if (!qs) return {};
		const out: Record<string, string> = {};
		new URLSearchParams(qs).forEach((value, key) => {
			out[key] = value;
		});
		return out;
	}

	private pickHighestSeverityVisible(actions: TenantActionItem[]): TenantActionItem | null {
		const visible = actions.filter((a) => !this.isDismissed(a));
		if (visible.length === 0) return null;
		return [...visible].sort((a, b) => SEVERITY_ORDER[b.severity] - SEVERITY_ORDER[a.severity])[0];
	}

	private isDismissed(action: TenantActionItem): boolean {
		try {
			return sessionStorage.getItem(this.dismissKey(action)) === '1';
		} catch {
			return false;
		}
	}

	private dismissKey(action: TenantActionItem): string {
		return `tenant-action-banner-dismissed:${action.type}`;
	}
}
